import * as React from 'react'
import { tenant } from '@/lib/tenant'

type Theme = 'light' | 'dark'

interface ThemeContextType {
	theme: Theme
	resolvedTheme: Theme
	setTheme: (theme: Theme) => void
	/** Whether the user is allowed to switch themes. Mirrors `tenant.themeToggle`. */
	canToggle: boolean
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(
	undefined
)

export function useTheme() {
	const context = React.useContext(ThemeContext)
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider')
	}
	return context
}

// Tenant config opts out by setting themeToggle: false → app is locked to defaultTheme.
const canToggle = tenant.themeToggle !== false
const defaultTheme: Theme = tenant.defaultTheme === 'light' ? 'light' : 'dark'

export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const [theme, setThemeState] = React.useState<Theme>(() => {
		if (!canToggle) return defaultTheme
		if (typeof window !== 'undefined') {
			const stored = localStorage.getItem('theme') as Theme | null
			return stored || defaultTheme
		}
		return defaultTheme
	})

	const setTheme = React.useCallback((newTheme: Theme) => {
		// When toggling is disabled, ignore any attempt to change the theme.
		if (!canToggle) return
		setThemeState(newTheme)
		localStorage.setItem('theme', newTheme)
	}, [])

	React.useEffect(() => {
		const root = document.documentElement
		root.classList.remove('light', 'dark')
		root.classList.add(theme)
	}, [theme])

	// Set initial theme class immediately on mount
	React.useEffect(() => {
		const initial: Theme = !canToggle
			? defaultTheme
			: ((localStorage.getItem('theme') as Theme | null) || defaultTheme)
		document.documentElement.classList.remove('light', 'dark')
		document.documentElement.classList.add(initial)

		// index.html ships a generic theme-color — sync it to the tenant's.
		if (tenant.themeColor) {
			document
				.querySelector('meta[name="theme-color"]')
				?.setAttribute('content', tenant.themeColor)
		}
	}, [])

	const value = React.useMemo(
		() => ({ theme, resolvedTheme: theme, setTheme, canToggle }),
		[theme, setTheme]
	)

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	)
}
